import React from "react";
import { Button } from "antd";
import {
  CalendarOutlined,
  ArrowRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";

/* ---------- shared pieces ---------- */

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
`;

const Meta = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
`;

const Dot = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 6px;
`;

const ViewEventButton = styled(Button)`
  background: #ffffff !important;
  color: #4750f5 !important;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: none;
  flex-shrink: 0;

  &:hover,
  &:focus {
    background: #f0f1ff;
    color: #4750f5;
  }
`;

const CloseIcon = styled(CloseOutlined)`
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.9;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`;

/* ---------- desktop layout (unchanged) ---------- */

const Bar = styled.div`
  width: 100%;
  min-height: 48px;
  background: #2d53da;
  align-items: center;
  justify-content: space-between;
  padding: 0 10% 0 8%;
  box-sizing: border-box;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial,
    sans-serif;

  display: flex;

  @media (max-width: 640px) {
    display: none;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #ffffff;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

/* ---------- mobile layout (new, matches screenshot) ---------- */

const MobileBar = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    background: #2d53da;
    padding: 12px 16px 14px;
    box-sizing: border-box;
    font-family:
      -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial,
      sans-serif;
  }
`;

const MobileTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const MobileBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  position: relative;
`;

const MobileTextGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #ffffff;
  text-align: center;
`;

const MobileCloseWrap = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
`;

/* ---------- component ---------- */

export default function EventHeader({
  eventName = "The Future of Workplace",
  date = "Saturday, 25 July 2026",
  locationname = "Nairobi",
  onView,
  onClose,
  setScrollItem,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleViewEvent = (e) => {
    e.preventDefault();
    const targetId = "event-forum";
    const buffer = 20;

    if (location.pathname === "/") {
      const el = document.getElementById(targetId);
      if (el) {
        const header = document.querySelector("header");
        const headerHeight = header?.offsetHeight || 96;
        const elementTop = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementTop - (headerHeight + buffer),
          behavior: "smooth",
        });
      }
    } else {
      navigate(`/#${targetId}`);
    }

    setScrollItem && setScrollItem("event");
  };

  const infoContent = (
    <>
      <Title>{eventName}</Title>
      <Dot>●</Dot>
      <Meta>{date}</Meta>
      <Dot>●</Dot>
      <Meta>{locationname}</Meta>
    </>
  );

  return (
    <>
      {/* Desktop / tablet layout */}
      <Bar>
        <Left>
          <Badge>
            <CalendarOutlined />
            Upcoming Event
          </Badge>
          {infoContent}
        </Left>

        <Right>
          <ViewEventButton onClick={handleViewEvent}>
            View Event <ArrowRightOutlined />
          </ViewEventButton>
          <CloseIcon onClick={onClose} />
        </Right>
      </Bar>

      {/* Mobile layout */}
      <MobileBar>
        <MobileTopRow>
          <Badge>
            <CalendarOutlined />
            Upcoming Event
          </Badge>
          <ViewEventButton onClick={handleViewEvent}>
            View Event <ArrowRightOutlined />
          </ViewEventButton>
        </MobileTopRow>

        <MobileBottomRow>
          <MobileTextGroup>{infoContent}</MobileTextGroup>
          <MobileCloseWrap>
            <CloseIcon onClick={onClose} />
          </MobileCloseWrap>
        </MobileBottomRow>
      </MobileBar>
    </>
  );
}
